import { Chart, registerables } from 'chart.js';

// chart.js v3 ist modular – alle Standard-Controller/Scales/Plugins registrieren.
Chart.register(...registerables);

// ── Typen ─────────────────────────────────────────────────────────────────────

export interface IAgeCount {
    age: number;
    count: number;
}

export interface IAgeSeries {
    years: number;
    ages: IAgeCount[];
    unknown: number;
    total: number;
    /** Optionale Landesverband-Identität (nur bei LV-übergreifender Auswertung gesetzt). */
    landesverbandId?: string;
    /** Anzeigename des Landesverbands (zur Beschriftung der Datenreihe im Diagramm). */
    landesverbandName?: string;
}

/** Auswahloption eines Landesverbands für die Diagramm-Steuerleiste. */
export interface ILandesverbandOption {
    id:   string;
    name: string;
}

/** Backend-Kontrakt: JSON von ONV.ONV.Newcomer.GetAgeDistribution */
export interface IAgeDistributionData {
    generatedAt?: string;
    series: IAgeSeries[];
}

interface IChartModel {
    labels: string[];
    datasets: { label: string; data: number[]; backgroundColor: string }[];
}

interface IBucket {
    label: string;
    min: number;
    max: number;
}

// ── Konstanten ────────────────────────────────────────────────────────────────

const UNKNOWN_LABEL  = 'Keine Angabe';
const GROUP_TOP      = 65;
const GROUP_BOTTOM   = 10;
const SERIES_COLORS  = ['#0057a8', '#e08a00', '#2e9e5b', '#a8324f', '#6a4fb3', '#0b8c8c'];

// ── Shadow-DOM Template ───────────────────────────────────────────────────────

const TEMPLATE = document.createElement('template');
TEMPLATE.innerHTML = /* html */`
<style>
  :host {
    display: block;
    font-family: inherit;
    font-size: var(--fs-onv-bar-char, 1rem);
    color: #222;
  }

  /* ---- Steuerleiste ---- */
  .onv-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 20px 32px;
    margin-bottom: 18px;
  }

  .onv-control {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  /* Die Landesverband-Auswahl sitzt rechts neben den Altersgruppen, solange Platz
     ist; reicht der Platz nicht, bricht sie als ganze Einheit in die nächste Zeile um.
     min-width verhindert ein zu starkes Zusammenquetschen vor dem Umbruch. */
  .onv-control--lv {
    flex: 1 1 auto;
    min-width: 240px;
  }

  .onv-control__label {
    font-size: .75em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #777;
  }

  .onv-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .onv-chip {
    font: inherit;
    font-size: .85em;
    padding: 5px 12px;
    border: 1px solid #c4c4c4;
    border-radius: 999px;
    background: #fff;
    color: #444;
    cursor: pointer;
    transition: all .12s;
  }

  .onv-chip:hover {
    border-color: #0057a8;
    color: #0057a8;
  }

  .onv-chip--active {
    background: #0057a8;
    border-color: #0057a8;
    color: #fff;
  }

  /* ---- Canvas-Bereich ---- */
  /* Feste, vom Breitenmaß entkoppelte Höhe: das Diagramm behält auch bei schmaler
     Darstellung eine sinnvolle Höhe (füllt den Card-Bereich), statt flach
     zusammenzufallen. Wächst leicht mit der Viewport-Höhe, mit Ober-/Untergrenze. */
  .onv-chart-canvas {
    position: relative;
    width: 100%;
    height: clamp(380px, 52vh, 640px);
  }

  /* Lade-, Leer- und Fehlerzustand liegen als Overlay über dem Canvas. */
  .onv-chart-overlay {
    position: absolute;
    inset: 0;
    display: none;            /* via JS auf "flex" gesetzt wenn aktiv */
    align-items: center;
    justify-content: center;
    text-align: center;
    background: rgba(255, 255, 255, .9);
    color: #777;
    font-size: .95em;
    padding: 0 16px;
  }

  .onv-chart-overlay--error {
    color: #d32f2f;
  }
</style>

<div class="onv-controls">
  <div class="onv-control">
    <span class="onv-control__label">Zeithorizont</span>
    <div class="onv-chips" id="horizon-chips"></div>
  </div>
  <div class="onv-control">
    <span class="onv-control__label">Altersgruppen</span>
    <div class="onv-chips" id="bucket-chips"></div>
  </div>
  <!-- Landesverband-Auswahl: nur sichtbar, wenn Optionen gesetzt sind. -->
  <div class="onv-control onv-control--lv" id="lv-control" style="display:none">
    <span class="onv-control__label">Landesverbände</span>
    <div class="onv-chips" id="lv-chips"></div>
  </div>
</div>

<div class="onv-chart-canvas">
  <canvas id="chart-canvas"></canvas>
  <div class="onv-chart-overlay" id="overlay-loading">
    <span>Daten werden geladen …</span>
  </div>
  <div class="onv-chart-overlay onv-chart-overlay--error" id="overlay-error"></div>
  <div class="onv-chart-overlay" id="overlay-empty">
    <span>Keine Altersdaten vorhanden.</span>
  </div>
</div>
`;

// ── Web Component ─────────────────────────────────────────────────────────────

/**
 * `<onv-age-bar-chart>` – gruppiertes Balkendiagramm für Newcomer-Altersverteilungen.
 *
 * ## Konfiguration
 *
 * ### 1) HTML-Attribute (statische Konfiguration, z. B. im Template)
 * | Attribut              | Typ             | Standard    | Beschreibung                          |
 * |-----------------------|-----------------|-------------|---------------------------------------|
 * | `horizon-options`     | `"3,6,9,12"`    | "3,6,9,12"  | Verfügbare Zeithorizonte (Jahre)       |
 * | `bucket-size-options` | `"5,10"`        | "5,10"      | Verfügbare Altersgruppen-Breiten       |
 * | `default-horizons`    | `"3,6"`         | "3,6"       | Vorausgewählte Zeithorizonte           |
 * | `default-bucket-size` | `"5"`           | "5"         | Vorausgewählte Altersgruppen-Breite    |
 * | `loading`             | Boolean-Attr.   | –           | Zeigt Ladeoverlay an                   |
 * | `error`               | Text            | –           | Zeigt Fehleroverlay mit diesem Text an |
 *
 * ### 2) JS-Property (reaktive Daten, z. B. nach Backend-Abfrage)
 * ```ts
 * const el = document.querySelector('onv-age-bar-chart') as ONVAgeBarChartElement;
 * el.data = { series: [...] };   // löst sofortiges Neu-Rendering aus
 * ```
 *
 * ### 3) Custom Event (Nutzerinteraktion nach außen melden)
 * ```ts
 * el.addEventListener('horizons-change', (e: CustomEvent<{ horizons: number[] }>) => {
 *     console.log('Neue Zeithorizonte:', e.detail.horizons);
 * });
 * ```
 * Das Event durchdringt Shadow-DOM-Grenzen (`composed: true`).
 */
export class ONVAgeBarChartElement extends HTMLElement {

    // ── Attribut-Namen (Konstanten zur Vermeidung von Tippfehlern) ────────────

    static readonly ATTR_LOADING         = 'loading';            // Boolean-Attribut
    static readonly ATTR_ERROR           = 'error';              // Fehlermeldung (Text)
    static readonly ATTR_HORIZON_OPTIONS = 'horizon-options';    // z. B. "3,6,9,12"
    static readonly ATTR_BUCKET_OPTIONS  = 'bucket-size-options';// z. B. "5,10"
    static readonly ATTR_DEFAULT_HORIZONS= 'default-horizons';   // z. B. "3,6"
    static readonly ATTR_DEFAULT_BUCKET  = 'default-bucket-size';// z. B. "5"
    static readonly ATTR_OVERLAY_HORIZONS= 'overlay-horizons';   // "true" → Horizonte je LV überlagern

    /** Ausgelöst wenn der Nutzer Zeithorizonte ändert: `detail = { horizons: number[] }` */
    static readonly EVENT_HORIZONS_CHANGE = 'horizons-change';

    /** Ausgelöst wenn der Nutzer die Landesverband-Auswahl ändert: `detail = { landesverbandIds: string[] }` */
    static readonly EVENT_LV_CHANGE = 'landesverbaende-change';

    static get observedAttributes(): string[] {
        return [
            ONVAgeBarChartElement.ATTR_LOADING,
            ONVAgeBarChartElement.ATTR_ERROR,
            ONVAgeBarChartElement.ATTR_HORIZON_OPTIONS,
            ONVAgeBarChartElement.ATTR_BUCKET_OPTIONS,
            ONVAgeBarChartElement.ATTR_DEFAULT_HORIZONS,
            ONVAgeBarChartElement.ATTR_DEFAULT_BUCKET,
            ONVAgeBarChartElement.ATTR_OVERLAY_HORIZONS,
        ];
    }

    // ── Interner State ────────────────────────────────────────────────────────

    private _chart: Chart | null = null;
    private _data: IAgeDistributionData | null = null;
    private _selectedHorizons: number[] = [3, 6];
    private _bucketSize: number = 5;
    private _horizonOptions: number[] = [3, 6, 9, 12];
    private _bucketSizeOptions: number[] = [5, 10];
    private _connected = false;
    /** Zeithorizonte je Landesverband überlagern (gestapelte Zuwachs-Segmente). */
    private _overlayHorizons = false;

    // Landesverband-Auswahl (rein über JS-Property, da dynamisch geladen).
    private _landesverbandOptions: ILandesverbandOption[] = [];
    private _selectedLvIds: string[] = [];
    /** Verhindert, dass die Standardauswahl (alle) eine spätere leere Nutzerauswahl überschreibt. */
    private _lvSelectionInitialised = false;

    // ── Public API: JS-Property ───────────────────────────────────────────────

    /**
     * Setzt die Rohdaten der Altersverteilung.
     * Löst automatisch ein Neu-Rendering des Diagramms und der Overlays aus.
     */
    set data(value: IAgeDistributionData | null) {
        this._data = value;
        if (this._connected) {
            this._renderChart();
            this._updateOverlays();
        }
    }

    get data(): IAgeDistributionData | null {
        return this._data;
    }

    /**
     * Setzt die auswählbaren Landesverbände. Beim ersten Setzen einer nicht-leeren Liste
     * werden alle Landesverbände vorausgewählt ("alle Landesverbands-Statistiken").
     * Spätere Aktualisierungen behalten die bestehende Auswahl (gefiltert auf noch
     * vorhandene Optionen).
     */
    set landesverbandOptions(value: ILandesverbandOption[] | null) {
        this._landesverbandOptions = (value ?? []).filter(o => o && o.id != null);

        const validIds = new Set(this._landesverbandOptions.map(o => o.id));

        if (!this._lvSelectionInitialised && this._landesverbandOptions.length > 0) {
            // Standard: alle Landesverbände aktiv.
            this._selectedLvIds = this._landesverbandOptions.map(o => o.id);
            this._lvSelectionInitialised = true;
        } else {
            // Auswahl auf weiterhin vorhandene Optionen einschränken.
            this._selectedLvIds = this._selectedLvIds.filter(id => validIds.has(id));
        }

        if (this._connected) this._renderLvChips();
    }

    get landesverbandOptions(): ILandesverbandOption[] {
        return this._landesverbandOptions;
    }

    /** Aktuell ausgewählte Landesverband-IDs (Kopie). */
    get selectedLandesverbaende(): string[] {
        return this._selectedLvIds.slice();
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    connectedCallback(): void {
        const root = this.attachShadow({ mode: 'open' });
        root.appendChild(TEMPLATE.content.cloneNode(true));
        this._connected = true;

        this._initChart();
        this._renderHorizonChips();
        this._renderBucketChips();
        this._renderLvChips();
        this._updateOverlays();

        // Falls data/Attribute vor connectedCallback gesetzt wurden: jetzt anwenden.
        if (this._data) this._renderChart();
    }

    disconnectedCallback(): void {
        this._chart?.destroy();
        this._chart = null;
        this._connected = false;
    }

    attributeChangedCallback(name: string, _old: string | null, newVal: string | null): void {
        switch (name) {
            case ONVAgeBarChartElement.ATTR_LOADING:
            case ONVAgeBarChartElement.ATTR_ERROR:
                if (this._connected) this._updateOverlays();
                break;

            case ONVAgeBarChartElement.ATTR_HORIZON_OPTIONS:
                this._horizonOptions = this._parseNumbers(newVal, [3, 6, 9, 12]);
                if (this._connected) this._renderHorizonChips();
                break;

            case ONVAgeBarChartElement.ATTR_BUCKET_OPTIONS:
                this._bucketSizeOptions = this._parseNumbers(newVal, [5, 10]);
                if (this._connected) this._renderBucketChips();
                break;

            case ONVAgeBarChartElement.ATTR_DEFAULT_HORIZONS:
                this._selectedHorizons = this._parseNumbers(newVal, [3, 6]);
                if (this._connected) this._renderHorizonChips();
                break;

            case ONVAgeBarChartElement.ATTR_DEFAULT_BUCKET:
                this._bucketSize = newVal != null ? Number(newVal) : 5;
                if (this._connected) {
                    this._renderBucketChips();
                    this._renderChart();
                }
                break;

            case ONVAgeBarChartElement.ATTR_OVERLAY_HORIZONS:
                this._overlayHorizons = newVal != null && newVal !== 'false';
                if (this._connected) this._renderChart();
                break;
        }
    }

    // ── Chart-Initialisierung ─────────────────────────────────────────────────
    private _initChart(): void {
        const canvas = this.shadowRoot!.getElementById('chart-canvas') as HTMLCanvasElement;

        // Basis-Schriftgröße ermitteln: bereits konfigurierter Wert, sonst der
        // Chart.js-Standard (12px). Anschließend um 1/3 verkleinern (→ 2/3 der Basis).
        const baseFontSize = Chart.defaults.font.size ?? 12;
        const fontSize     = 12;

        this._chart = new Chart(canvas, {
            type: 'bar',
            data: { labels: [], datasets: [] },
            options: {
                responsive: true,
                // Höhe NICHT aus der Breite ableiten: das Diagramm füllt die feste Höhe
                // des Canvas-Bereichs (.onv-chart-canvas) und fällt bei schmaler
                // Darstellung nicht flach zusammen.
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        title: { display: true, text: 'Anzahl Newcomer', font: { size: fontSize } },
                        ticks: { precision: 0, font: { size: fontSize } },
                    },
                    x: {
                        title: { display: true, text: 'Altersgruppe', font: { size: fontSize } },
                        ticks: { font: { size: fontSize } },
                    },
                },
                plugins: {
                    legend: { display: true, position: 'top', labels: { font: { size: fontSize } } },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        titleFont: { size: fontSize },
                        bodyFont:  { size: fontSize },
                        callbacks: {
                            // Im Überlagerungs-Modus sind die Segmente Zuwächse; im Tooltip
                            // soll aber die KUMULATIVE Zahl ("Letzte N Jahre") stehen. Diese
                            // wird je Dataset in `cumulative` mitgeführt.
                            label: (ctx: any) => {
                                const ds = ctx.dataset as any;
                                const value = Array.isArray(ds.cumulative)
                                    ? ds.cumulative[ctx.dataIndex]
                                    : ctx.parsed.y;
                                return `${ctx.dataset.label}: ${value}`;
                            },
                        },
                    },
                },
            },
        });
    }

    // ── Rendering ─────────────────────────────────────────────────────────────

    private _renderChart(): void {
        if (!this._chart || !this._data) return;
        const model = this._buildChartModel(this._data.series, this._bucketSize);

        // Überlagerung wird über gestapelte Achsen umgesetzt (Segmente = Zuwächse).
        const scales = this._chart.options.scales as any;
        if (scales?.x) scales.x.stacked = this._overlayHorizons;
        if (scales?.y) scales.y.stacked = this._overlayHorizons;

        this._chart.data.labels   = model.labels;
        this._chart.data.datasets = model.datasets as any;
        // resize() + nächster Frame: korrekte Dimensionierung, auch wenn der
        // Canvas beim Erzeugen noch nicht sichtbar war.
        this._chart.resize();
        this._chart.update('none');
        requestAnimationFrame(() => { this._chart?.resize(); this._chart?.update('none'); });
    }

    private _updateOverlays(): void {
        if (!this._connected) return;
        const root    = this.shadowRoot!;
        const loading = this.hasAttribute(ONVAgeBarChartElement.ATTR_LOADING);
        const error   = this.getAttribute(ONVAgeBarChartElement.ATTR_ERROR);
        const hasData = this._data?.series?.some(s => s.total > 0) ?? false;

        const setVisible = (id: string, visible: boolean) => {
            (root.getElementById(id) as HTMLElement).style.display = visible ? 'flex' : 'none';
        };

        setVisible('overlay-loading', loading);
        setVisible('overlay-error',   !loading && !!error);
        setVisible('overlay-empty',   !loading && !error && !hasData);

        const errorEl = root.getElementById('overlay-error') as HTMLElement;
        errorEl.textContent = error ?? '';
    }

    private _renderHorizonChips(): void {
        const container = this.shadowRoot?.getElementById('horizon-chips');
        if (!container) return;
        container.innerHTML = '';
        for (const years of this._horizonOptions) {
            const btn = document.createElement('button');
            btn.type        = 'button';
            btn.className   = 'onv-chip' + (this._selectedHorizons.includes(years) ? ' onv-chip--active' : '');
            btn.textContent = years === 1 ? 'Letztes Jahr' : `Letzte ${years} J.`;
            btn.addEventListener('click', () => this._toggleHorizon(years));
            container.appendChild(btn);
        }
    }

    private _renderBucketChips(): void {
        const container = this.shadowRoot?.getElementById('bucket-chips');
        if (!container) return;
        container.innerHTML = '';
        for (const size of this._bucketSizeOptions) {
            const btn = document.createElement('button');
            btn.type        = 'button';
            btn.className   = 'onv-chip' + (this._bucketSize === size ? ' onv-chip--active' : '');
            btn.textContent = `${size}-Jahres-Gruppen`;
            btn.addEventListener('click', () => this._setBucketSize(size));
            container.appendChild(btn);
        }
    }

    private _renderLvChips(): void {
        const control   = this.shadowRoot?.getElementById('lv-control') as HTMLElement | null;
        const container = this.shadowRoot?.getElementById('lv-chips');
        if (!control || !container) return;

        // Steuerleiste nur einblenden, wenn überhaupt Landesverbände zur Wahl stehen.
        control.style.display = this._landesverbandOptions.length > 0 ? 'flex' : 'none';

        container.innerHTML = '';
        for (const opt of this._landesverbandOptions) {
            const btn = document.createElement('button');
            btn.type        = 'button';
            btn.className   = 'onv-chip' + (this._selectedLvIds.includes(opt.id) ? ' onv-chip--active' : '');
            btn.textContent = opt.name;
            btn.addEventListener('click', () => this._toggleLandesverband(opt.id));
            container.appendChild(btn);
        }
    }

    // ── Nutzer-Interaktion ────────────────────────────────────────────────────

    private _toggleLandesverband(id: string): void {
        const idx = this._selectedLvIds.indexOf(id);
        if (idx >= 0) {
            if (this._selectedLvIds.length <= 1) return; // min. 1 aktiv
            this._selectedLvIds = this._selectedLvIds.filter(x => x !== id);
        } else {
            // Auswahl in der Reihenfolge der Optionen halten (stabile Farben/Reihenfolge).
            const order = this._landesverbandOptions.map(o => o.id);
            this._selectedLvIds = order.filter(x => this._selectedLvIds.includes(x) || x === id);
        }
        this._renderLvChips();
        this.dispatchEvent(new CustomEvent(ONVAgeBarChartElement.EVENT_LV_CHANGE, {
            detail:   { landesverbandIds: this._selectedLvIds.slice() },
            bubbles:  true,
            composed: true,
        }));
    }

    private _toggleHorizon(years: number): void {
        const idx = this._selectedHorizons.indexOf(years);
        if (idx >= 0) {
            if (this._selectedHorizons.length <= 1) return; // min. 1 aktiv
            this._selectedHorizons = this._selectedHorizons.filter(h => h !== years);
        } else {
            this._selectedHorizons = [...this._selectedHorizons, years].sort((a, b) => a - b);
        }
        this._renderHorizonChips();
        // Elternkomponente per Event informieren – sie holt dann neue Daten.
        this.dispatchEvent(new CustomEvent(ONVAgeBarChartElement.EVENT_HORIZONS_CHANGE, {
            detail:   { horizons: this._selectedHorizons.slice() },
            bubbles:  true,
            composed: true, // durchdringt Shadow-DOM-Grenze
        }));
    }

    private _setBucketSize(size: number): void {
        this._bucketSize = size;
        this._renderBucketChips();
        this._renderChart(); // rein clientseitig, keine neue Abfrage
    }

    // ── Diagramm-Logik ────────────────────────────────────────────────────────

    private _buildChartModel(series: IAgeSeries[], bucketSize: number): IChartModel {
        if (series.length === 0) return { labels: [], datasets: [] };

        const buckets     = this._buildBuckets(bucketSize);
        // "<10"-Gruppe nur einblenden, wenn dort tatsächlich Daten liegen.
        const hasLowAges  = series.some(s => s.ages.some(a => a.age > 0 && a.age < GROUP_BOTTOM && a.count > 0));
        const usedBuckets = buckets.filter(b => b.min >= GROUP_BOTTOM || hasLowAges);
        // "Keine Angabe" nur einblenden, wenn vorhanden.
        const anyUnknown  = series.some(s => s.unknown > 0);

        const labels = usedBuckets.map(b => b.label);
        if (anyUnknown) labels.push(UNKNOWN_LABEL);

        return this._overlayHorizons
            ? this._buildOverlayDatasets(series, usedBuckets, anyUnknown, labels)
            : this._buildGroupedDatasets(series, usedBuckets, anyUnknown, labels);
    }

    /** Beschriftung einer Serie: mit Landesverband (falls vorhanden), sonst nur Horizont. */
    private _seriesLabel(s: IAgeSeries): string {
        const horizon = s.years === 1 ? 'Letztes Jahr' : `Letzte ${s.years} Jahre`;
        return s.landesverbandName ? `${s.landesverbandName} · ${horizon}` : horizon;
    }

    /** Kumulative Zählwerte je sichtbarem Eimer (inkl. optionalem "Keine Angabe"). */
    private _seriesValues(s: IAgeSeries, usedBuckets: IBucket[], anyUnknown: boolean): number[] {
        const v = usedBuckets.map(b => this._sumAges(s.ages, b.min, b.max));
        if (anyUnknown) v.push(s.unknown);
        return v;
    }

    /** Standard-Darstellung: je Serie ein nebeneinanderstehender Balken. */
    private _buildGroupedDatasets(
        series: IAgeSeries[], usedBuckets: IBucket[], anyUnknown: boolean, labels: string[],
    ): IChartModel {
        const datasets = series.map((s, idx) => ({
            label:           this._seriesLabel(s),
            data:            this._seriesValues(s, usedBuckets, anyUnknown),
            backgroundColor: this._seriesColor(idx, series.length),
        }));
        return { labels, datasets };
    }

    /**
     * Überlagerungs-Darstellung: je Landesverband EIN gestapelter Balken, dessen Segmente
     * die Zeithorizonte sind. Da die Horizonte kumulativ verschachtelt sind (kürzere ⊆
     * längere), werden Zuwächse gestapelt – die Segment-Oberkante entspricht damit der
     * kumulativen "Letzte N Jahre"-Zahl (für den Tooltip in `cumulative` mitgeführt).
     * Farbe = Landesverband (Farbton), Helligkeit = Zeithorizont (kürzer = dunkler/unten).
     */
    private _buildOverlayDatasets(
        series: IAgeSeries[], usedBuckets: IBucket[], anyUnknown: boolean, labels: string[],
    ): IChartModel {
        // Serien je Landesverband gruppieren (Reihenfolge des ersten Auftretens beibehalten).
        const order:  string[] = [];
        const groups = new Map<string, IAgeSeries[]>();
        for (const s of series) {
            const key = s.landesverbandId ?? '__single__';
            if (!groups.has(key)) { groups.set(key, []); order.push(key); }
            groups.get(key)!.push(s);
        }

        const datasets: any[] = [];
        order.forEach((key, groupIdx) => {
            // Horizonte aufsteigend: kürzester unten (dunkel), längster oben (hell).
            const grp = groups.get(key)!.slice().sort((a, b) => a.years - b.years);
            let prev: number[] | null = null;

            grp.forEach((s, level) => {
                const cumulative = this._seriesValues(s, usedBuckets, anyUnknown);
                // Segment = Zuwachs gegenüber dem nächstkürzeren Horizont (>= 0).
                const increment = cumulative.map((c, i) => (prev ? Math.max(0, c - prev![i]) : c));
                prev = cumulative;

                datasets.push({
                    label:           this._seriesLabel(s),
                    data:            increment,
                    backgroundColor: this._overlayColor(groupIdx, order.length, level, grp.length),
                    stack:           `lv-${key}`,
                    cumulative,      // Tooltip zeigt die kumulative Zahl statt des Zuwachses.
                });
            });
        });

        return { labels, datasets };
    }

    /**
     * Liefert eine Diagrammfarbe für die idx-te Datenreihe (Standard-Darstellung). Bis zur
     * Länge der festen Palette werden deren Farben genutzt; darüber hinaus (viele LV ×
     * Zeithorizont-Kombinationen) werden gleichmäßig verteilte HSL-Farben erzeugt.
     */
    private _seriesColor(idx: number, count: number): string {
        if (count <= SERIES_COLORS.length) return SERIES_COLORS[idx % SERIES_COLORS.length];
        const hue = Math.round((360 / count) * idx);
        return `hsl(${hue}, 62%, 45%)`;
    }

    /**
     * Farbe in der Überlagerungs-Darstellung: Farbton kennzeichnet den Landesverband,
     * Helligkeit den Zeithorizont (kürzerer Horizont dunkler, längerer heller).
     */
    private _overlayColor(groupIdx: number, groupCount: number, level: number, levelCount: number): string {
        const hue = groupCount > 0 ? Math.round((360 / groupCount) * groupIdx) : 210;
        const LIGHT_MIN = 30, LIGHT_MAX = 70;
        const light = levelCount <= 1
            ? 46
            : LIGHT_MIN + (LIGHT_MAX - LIGHT_MIN) * (level / (levelCount - 1));
        return `hsl(${hue}, 58%, ${light}%)`;
    }

    private _buildBuckets(size: number): IBucket[] {
        const buckets: IBucket[] = [
            { label: `<${GROUP_BOTTOM}`, min: 1, max: GROUP_BOTTOM - 1 },
        ];

        let start = GROUP_BOTTOM;
        while (start < GROUP_TOP) {
            const upper = start + size - 1;
            if (upper >= GROUP_TOP) break;
            buckets.push({ label: `${start}–${upper}`, min: start, max: upper });
            start += size;
        }
        buckets.push({ label: `${start}+`, min: start, max: Number.POSITIVE_INFINITY });

        return buckets;
    }

    private _sumAges(ages: IAgeCount[], min: number, max: number): number {
        let sum = 0;
        for (const a of ages) {
            if (a.age >= min && a.age <= max) sum += a.count;
        }
        return sum;
    }

    // ── Hilfsmethoden ────────────────────────────────────────────────────────

    private _parseNumbers(value: string | null, fallback: number[]): number[] {
        if (!value) return fallback;
        const parsed = value.split(',').map(Number).filter(n => !isNaN(n) && n > 0);
        return parsed.length > 0 ? parsed : fallback;
    }
}

// Registrierung (idempotent – safe bei mehrfachem Import)
if (!customElements.get('onv-age-bar-chart')) {
    customElements.define('onv-age-bar-chart', ONVAgeBarChartElement);
}
